import { CHEST_SIZE, type ChestState } from "../player/chest";
import { CraftingGrid } from "../player/crafting";
import type { FurnaceState } from "../player/furnace";
import {
  ARMOR_SLOT_COUNT,
  ARMOR_SLOT_NAMES,
  HOTBAR_SIZE,
  INVENTORY_SIZE,
  type Inventory,
} from "../player/inventory";
import { describeStack, stackLimit, type ArmorSlot, type ItemStack } from "../world/items";
import { clearBlockIcon, drawEmptyArmorSlotIcon, drawItemIcon } from "./icons";
import { createDurabilityBar, updateDurability, type DurabilityView } from "./durability";
import { sound } from "../audio/sound-engine";

export type InventoryMode = "inventory" | "table" | "furnace" | "chest";

const CRAFT_SIZE: Record<"inventory" | "table", 2 | 3> = { inventory: 2, table: 3 };

interface SlotView {
  readonly el: HTMLElement;
  readonly icon: HTMLCanvasElement;
  readonly count: HTMLElement;
  readonly durability: DurabilityView;
  iconId: number;
}

/**
 * Survival inventory screen, also used for the crafting table, furnace and
 * chest.
 *
 * A 27-slot storage grid and the nine hotbar slots sit on the left; a 2x2
 * crafting grid (3x3 at a table) and its result slot sit on the right. Furnaces
 * swap that panel for input/fuel/output, and chests show a 27-slot grid above
 * the pack. Clicking moves a cursor stack around: left takes/places/merges/
 * swaps, right takes half or places one, Shift-click quick-moves. The screen
 * mutates the injected `Inventory` and reports every change through `onChange`.
 */
export class InventoryScreen {
  open = false;
  /** Fired when the player asks to close (the Close button). */
  onClose: (() => void) | null = null;
  /** Fired after any inventory or crafting change, so the HUD can refresh. */
  onChange: (() => void) | null = null;

  private inventory: Inventory | null = null;
  private crafting: CraftingGrid | null = null;
  private furnace: FurnaceState | null = null;
  private chest: ChestState | null = null;
  private mode: InventoryMode = "inventory";
  private held: ItemStack | null = null;
  private mouseX = 0;
  private mouseY = 0;

  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly hintEl: HTMLElement;
  private readonly storageEl: HTMLElement;
  private readonly hotbarEl: HTMLElement;
  private readonly inventoryRightEl: HTMLElement;
  private readonly chestAreaEl: HTMLElement;
  private readonly chestGridEl: HTMLElement;
  private readonly armorAreaEl: HTMLElement;
  private readonly armorGridEl: HTMLElement;
  private readonly craftingAreaEl: HTMLElement;
  private readonly craftGridEl: HTMLElement;
  private readonly resultEl: HTMLElement;
  private readonly furnaceAreaEl: HTMLElement;
  private readonly flameFill: HTMLElement;
  private readonly progressFill: HTMLElement;
  private readonly heldEl: HTMLElement;

  private readonly slotViews: SlotView[] = [];
  private readonly armorViews: SlotView[] = [];
  private craftViews: SlotView[] = [];
  private readonly furnaceViews: SlotView[] = [];
  private readonly chestViews: SlotView[] = [];
  private readonly resultView: SlotView;
  private readonly heldView: SlotView;

  constructor(root: HTMLElement) {
    this.root = must(root.querySelector<HTMLElement>("#inventory"));
    this.titleEl = must(root.querySelector<HTMLElement>("#inventory-title"));
    this.hintEl = must(root.querySelector<HTMLElement>("#inventory-hint"));
    this.storageEl = must(root.querySelector<HTMLElement>("#inventory-storage"));
    this.hotbarEl = must(root.querySelector<HTMLElement>("#inventory-hotbar"));
    this.inventoryRightEl = must(root.querySelector<HTMLElement>("#inventory-right"));
    this.chestAreaEl = must(root.querySelector<HTMLElement>("#chest-area"));
    this.chestGridEl = must(root.querySelector<HTMLElement>("#chest-grid"));
    this.armorAreaEl = must(root.querySelector<HTMLElement>("#armor-area"));
    this.armorGridEl = must(root.querySelector<HTMLElement>("#armor-grid"));
    this.craftingAreaEl = must(root.querySelector<HTMLElement>("#crafting-area"));
    this.craftGridEl = must(root.querySelector<HTMLElement>("#crafting-grid"));
    this.resultEl = must(root.querySelector<HTMLElement>("#crafting-result"));
    this.furnaceAreaEl = must(root.querySelector<HTMLElement>("#furnace-area"));
    this.flameFill = must(root.querySelector<HTMLElement>("#furnace-flame-fill"));
    this.progressFill = must(root.querySelector<HTMLElement>("#furnace-progress-fill"));
    this.heldEl = must(root.querySelector<HTMLElement>("#inventory-held"));

    const close = must(root.querySelector<HTMLButtonElement>("#inventory-close"));
    close.onclick = () => {
      sound.playUiClick();
      this.onClose?.();
    };

    // The screen markup is static; wipe it so a rebuilt HUD starts clean.
    this.storageEl.textContent = "";
    this.hotbarEl.textContent = "";
    this.chestGridEl.textContent = "";
    this.armorGridEl.textContent = "";
    this.craftGridEl.textContent = "";

    for (let i = 0; i < ARMOR_SLOT_COUNT; i++) {
      const view = buildSlotView();
      view.el.addEventListener("mousedown", (e) => this.onArmorSlotMouseDown(i, e));
      view.el.title = `Armor (${ARMOR_SLOT_NAMES[i]})`;
      this.armorGridEl.appendChild(view.el);
      this.armorViews.push(view);
    }

    for (let i = 0; i < INVENTORY_SIZE; i++) {
      const view = buildSlotView();
      view.el.addEventListener("mousedown", (e) => this.onSlotMouseDown(i, e));
      (i < HOTBAR_SIZE ? this.hotbarEl : this.storageEl).appendChild(view.el);
      this.slotViews.push(view);
    }

    for (let i = 0; i < CHEST_SIZE; i++) {
      const view = buildSlotView();
      view.el.addEventListener("mousedown", (e) => this.onChestSlotMouseDown(i, e));
      this.chestGridEl.appendChild(view.el);
      this.chestViews.push(view);
    }

    this.resultView = buildSlotView(this.resultEl);
    this.resultEl.onmousedown = (e) => this.onResultMouseDown(e);

    const furnaceSlots = ["#furnace-input", "#furnace-fuel", "#furnace-output"] as const;
    furnaceSlots.forEach((selector, index) => {
      const view = buildSlotView(must(root.querySelector<HTMLElement>(selector)));
      view.el.addEventListener("mousedown", (e) => this.onFurnaceSlotMouseDown(index, e));
      this.furnaceViews.push(view);
    });

    this.heldView = buildSlotView(this.heldEl);

    // `on*` properties, not listeners: the markup persists across worlds.
    this.root.oncontextmenu = (e) => e.preventDefault();
    this.root.onmousemove = (e) => {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
      this.positionHeld();
    };
  }

  show(
    inventory: Inventory,
    mode: InventoryMode = "inventory",
    furnace: FurnaceState | null = null,
    chest: ChestState | null = null,
  ): void {
    // Rebuilding while open must not eat the cursor stack or grid contents.
    if (this.open) this.returnContents();

    this.inventory = inventory;
    this.mode = mode;
    this.furnace = furnace;
    this.chest = chest;
    const furnaceMode = mode === "furnace";
    const chestMode = mode === "chest";
    this.titleEl.textContent = furnaceMode
      ? "Furnace"
      : chestMode
        ? "Chest"
        : mode === "table"
          ? "Crafting Table"
          : "Inventory";
    this.hintEl.textContent = furnaceMode
      ? "Smelt ore, sand, cobble or logs · coal and charcoal burn longest\nClick to move · right-click half / one · Shift-click quick-move"
      : chestMode
        ? "Click to move · right-click half / one · Shift-click transfer\nContents stay in the chest while it is closed"
        : mode === "table"
          ? "Pickaxe MMM/.S./.S. · Axe MM/MS/.S · Shovel M/S/S\nM = planks, cobblestone or iron ingots · S = stick · click result · Shift-click crafts all"
          : "Click to move · right-click half / one · Shift-click quick-move\nLog → 4 Planks · 2 Planks → 4 Sticks · 4 Planks → Table";
    this.held = null;
    this.armorAreaEl.hidden = furnaceMode || chestMode;
    this.craftingAreaEl.hidden = furnaceMode || chestMode;
    this.furnaceAreaEl.hidden = !furnaceMode;
    this.chestAreaEl.hidden = !chestMode;
    this.inventoryRightEl.hidden = chestMode;

    if (furnaceMode || chestMode) {
      this.crafting = null;
      this.craftGridEl.textContent = "";
      this.craftViews = [];
    } else {
      this.buildCrafting(mode === "table" ? CRAFT_SIZE.table : CRAFT_SIZE.inventory);
    }

    this.root.hidden = false;
    this.open = true;
    this.render();
  }

  hide(): void {
    if (!this.open) return;
    this.returnContents();
    this.root.hidden = true;
    this.open = false;
    this.crafting = null;
    this.furnace = null;
    this.chest = null;
    this.inventory = null;
  }

  /** The active screen, so the game can tell a furnace from the pack. */
  get screenMode(): InventoryMode {
    return this.mode;
  }

  render(): void {
    const inventory = this.inventory;
    if (!inventory) return;

    for (let i = 0; i < this.slotViews.length; i++) {
      this.renderSlot(this.slotViews[i], inventory.slots[i] ?? null);
    }

    for (let i = 0; i < this.armorViews.length; i++) {
      this.renderArmorSlot(this.armorViews[i], inventory.armor[i] ?? null, ARMOR_SLOT_NAMES[i]);
    }

    if (this.mode === "furnace") {
      this.refreshFurnace();
    } else if (this.mode === "chest") {
      for (let i = 0; i < this.chestViews.length; i++) {
        this.renderSlot(this.chestViews[i], this.chest?.slotAt(i) ?? null);
      }
    } else {
      for (let i = 0; i < this.craftViews.length; i++) {
        this.renderSlot(this.craftViews[i], this.crafting?.cells[i] ?? null);
      }

      const result = this.crafting?.result() ?? null;
      this.renderSlot(this.resultView, result, false);
      this.resultEl.classList.toggle("ready", result !== null);
    }

    this.heldEl.hidden = this.held === null;
    this.renderSlot(this.heldView, this.held, false);
    if (this.held) this.positionHeld();
  }

  /** Cheap per-frame refresh while a furnace is smelting with its screen open. */
  refreshFurnace(): void {
    const furnace = this.furnace;
    if (!furnace || !this.open || this.mode !== "furnace") return;

    for (let i = 0; i < this.furnaceViews.length; i++) {
      // Input and fuel show a single item's count; the output hides a "1".
      this.renderSlot(this.furnaceViews[i], furnace.slotAt(i), i < 2);
    }

    this.furnaceAreaEl.classList.toggle("lit", furnace.lit);
    this.progressFill.style.width = `${Math.round(Math.min(1, furnace.progress) * 100)}%`;
    const effectiveBurn = furnace.lit ? Math.max(0, furnace.burnRemaining - furnace.progress) : 0;
    this.flameFill.style.height = `${Math.round(
      furnace.burnTotal > 0 ? Math.max(0, Math.min(1, effectiveBurn / furnace.burnTotal)) * 100 : 0,
    )}%`;
    this.furnaceViews[2].el.classList.toggle("ready", furnace.slotAt(2) !== null);
  }

  private buildCrafting(size: 2 | 3): void {
    this.crafting = new CraftingGrid(size);
    this.craftGridEl.textContent = "";
    this.craftGridEl.style.gridTemplateColumns = `repeat(${size}, 36px)`;
    this.craftViews = [];

    for (let i = 0; i < size * size; i++) {
      const view = buildSlotView();
      view.el.addEventListener("mousedown", (e) => this.onCraftCellMouseDown(i, e));
      this.craftGridEl.appendChild(view.el);
      this.craftViews.push(view);
    }
  }

  /** Put the cursor stack and every crafting-cell stack back into the pack. */
  private returnContents(): void {
    const inventory = this.inventory;
    if (!inventory) return;
    if (this.held) {
      inventory.addStack(this.held);
      this.held = null;
    }
    if (this.crafting) {
      for (const stack of this.crafting.drain()) inventory.addStack(stack);
    }
  }

  private renderSlot(view: SlotView, stack: ItemStack | null, showSingle = true): void {
    const id = stack?.id ?? 0;
    if (view.iconId !== id) {
      view.iconId = id;
      if (id > 0) drawItemIcon(view.icon, id);
      else clearBlockIcon(view.icon);
    }
    view.el.title = stack
      ? `${describeStack(stack)}${stack.count > 1 ? ` ×${stack.count}` : ""}`
      : "";
    view.count.textContent =
      stack && (showSingle || stack.count > 1) ? String(stack.count) : "";
    updateDurability(view.durability, stack);
  }

  private renderArmorSlot(view: SlotView, stack: ItemStack | null, slot: ArmorSlot): void {
    const id = stack?.id ?? 0;
    if (view.iconId !== id) {
      view.iconId = id;
      if (id > 0) {
        drawItemIcon(view.icon, id);
      } else {
        drawEmptyArmorSlotIcon(view.icon, slot);
      }
    }
    view.el.title = stack
      ? `${describeStack(stack)} (${slot})`
      : `Empty ${slot} slot`;
    view.count.textContent =
      stack && stack.count > 1 ? String(stack.count) : "";
    updateDurability(view.durability, stack);
  }

  private onArmorSlotMouseDown(index: number, e: MouseEvent): void {
    if (!this.inventory || !this.open) return;
    e.preventDefault();
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    if (e.button === 0 && e.shiftKey && !this.held) {
      this.inventory.quickMoveArmor(index);
    } else if (e.button === 0 || e.button === 2) {
      const button = e.button === 2 ? "right" : "left";
      this.held = this.inventory.clickArmorSlot(index, button, this.held);
    } else {
      return;
    }

    this.render();
    sound.playUiClick();
    this.onChange?.();
  }

  private onSlotMouseDown(index: number, e: MouseEvent): void {
    if (!this.inventory || !this.open) return;
    e.preventDefault();
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    if (e.button === 0 && e.shiftKey && !this.held) {
      // With a container open, Shift-click feeds it first, then falls back to
      // the usual hotbar <-> storage quick-move.
      const moved =
        (this.mode === "furnace" && this.shiftIntoFurnace(index)) ||
        (this.mode === "chest" && this.shiftIntoChest(index));
      if (!moved) this.inventory.quickMove(index);
    } else if (e.button === 0 || e.button === 2) {
      const button = e.button === 2 ? "right" : "left";
      this.held = this.inventory.clickSlot(index, button, this.held);
    } else {
      return;
    }

    this.render();
    sound.playUiClick();
    this.onChange?.();
  }

  private onCraftCellMouseDown(index: number, e: MouseEvent): void {
    if (!this.inventory || !this.crafting || !this.open) return;
    e.preventDefault();
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    if (e.button === 0 && e.shiftKey && !this.held) {
      const stack = this.crafting.cells[index] ?? null;
      if (stack) {
        this.crafting.cells[index] = null;
        this.inventory.addStack(stack);
      }
    } else if (e.button === 0 || e.button === 2) {
      const button = e.button === 2 ? "right" : "left";
      this.held = this.crafting.clickCell(index, button, this.held);
    } else {
      return;
    }

    this.render();
    sound.playUiClick();
    this.onChange?.();
  }

  private onFurnaceSlotMouseDown(index: number, e: MouseEvent): void {
    if (!this.inventory || !this.furnace || !this.open) return;
    e.preventDefault();
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    if (e.button === 0 && e.shiftKey && !this.held) {
      this.furnace.quickMove(index, this.inventory);
    } else if (e.button === 0 || e.button === 2) {
      const button = e.button === 2 ? "right" : "left";
      this.held = this.furnace.clickSlot(index, button, this.held);
    } else {
      return;
    }

    this.render();
    sound.playUiClick();
    this.onChange?.();
  }

  private onChestSlotMouseDown(index: number, e: MouseEvent): void {
    if (!this.inventory || !this.chest || !this.open) return;
    e.preventDefault();
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    if (e.button === 0 && e.shiftKey && !this.held) {
      this.chest.quickMove(index, this.inventory);
    } else if (e.button === 0 || e.button === 2) {
      const button = e.button === 2 ? "right" : "left";
      this.held = this.chest.clickSlot(index, button, this.held);
    } else {
      return;
    }

    this.render();
    sound.playUiClick();
    this.onChange?.();
  }

  /** Move a stack from the pack into the chest; true if any moved. */
  private shiftIntoChest(index: number): boolean {
    const inventory = this.inventory;
    const chest = this.chest;
    const stack = inventory?.slots[index] ?? null;
    if (!inventory || !chest || !stack) return false;

    const moved = chest.insert(stack);
    if (moved <= 0) return false;

    if (moved >= stack.count) inventory.setSlot(index, null);
    else inventory.setSlot(index, { ...stack, count: stack.count - moved });
    return true;
  }

  /** Move a stack from the pack into the input or fuel slot; true if any moved. */
  private shiftIntoFurnace(index: number): boolean {
    const inventory = this.inventory;
    const furnace = this.furnace;
    const stack = inventory?.slots[index] ?? null;
    if (!inventory || !furnace || !stack) return false;

    const moved = furnace.insert(stack);
    if (moved <= 0) return false;

    if (moved >= stack.count) inventory.setSlot(index, null);
    else inventory.setSlot(index, { ...stack, count: stack.count - moved });
    return true;
  }

  private onResultMouseDown(e: MouseEvent): void {
    if (!this.inventory || !this.crafting || !this.open) return;
    e.preventDefault();
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    if (e.button !== 0 && e.button !== 2) return;

    if (e.shiftKey) this.craftAll();
    else this.craftOne();

    this.render();
    sound.playUiClick();
    this.onChange?.();
  }

  /** Craft one item onto the cursor, refusing if the cursor cannot hold it. */
  private craftOne(): void {
    if (!this.crafting) return;
    const result = this.crafting.result();
    if (!result) return;

    if (this.held) {
      if (this.held.id !== result.id) return;
      if (this.held.count + result.count > stackLimit(result.id)) return;
    }

    const crafted = this.crafting.takeResult();
    if (!crafted) return;
    this.held = this.held ? { ...this.held, count: this.held.count + crafted.count } : crafted;
  }

  /** Shift-click the result: keep crafting into the pack while a recipe fits. */
  private craftAll(): void {
    if (!this.inventory || !this.crafting) return;
    let guard = 0;
    while (guard++ < 256) {
      const crafted = this.crafting.takeResult();
      if (!crafted) break;
      this.inventory.addStack(crafted);
    }
  }

  private positionHeld(): void {
    if (!this.open || !this.held) return;
    this.heldEl.style.left = `${this.mouseX}px`;
    this.heldEl.style.top = `${this.mouseY}px`;
  }
}

function buildSlotView(existing?: HTMLElement): SlotView {
  const slot = existing ?? document.createElement("div");
  if (!existing) slot.className = "slot inv-slot";
  slot.textContent = "";

  const icon = document.createElement("canvas");
  icon.width = 32;
  icon.height = 32;
  icon.className = "slot-icon";

  const count = document.createElement("span");
  count.className = "slot-count";

  const durability = createDurabilityBar();

  slot.append(icon, count, durability.bar);
  return { el: slot, icon, count, durability, iconId: -1 };
}

function must<T extends Element>(el: T | null): T {
  if (!el) throw new Error("inventory element missing from index.html");
  return el;
}
